/**
 * The marketing-measurement consent gate.
 *
 * ONE decision, read by every measurement path there will ever be. The Meta
 * pixel must not load before it is granted, and when server-side events are
 * added they must read this same value: the Conversions API is a better
 * transport, not a way around a declined banner. If these two ever disagree,
 * the privacy policy's promise ("nu transmitem datele către terți fără acordul
 * dumneavoastră explicit") becomes false.
 *
 * Nothing here gates the map or the address search. Those are functionally
 * necessary to draw a parcel and are disclosed in the policy as processors;
 * asking permission to run the thing the page exists to do would be theatre.
 */

const STORAGE_KEY = 'prq.consent.marketing.v1';

export type ConsentState = 'granted' | 'denied' | 'unset';

type Listener = (state: ConsentState) => void;
const listeners = new Set<Listener>();

export function readConsent(): ConsentState {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === 'granted' || raw === 'denied' ? raw : 'unset';
  } catch {
    /*
     * Safari in private mode throws on localStorage rather than returning null.
     * Fall back to "undecided", never to "granted": a storage failure must not
     * become a silent opt-in. The cost is that the banner reappears; the
     * alternative is tracking someone who never agreed.
     */
    return 'unset';
  }
}

export function writeConsent(state: Exclude<ConsentState, 'unset'>): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, state);
  } catch {
    /* See readConsent. The decision still takes effect for this page view. */
  }
  listeners.forEach((fn) => fn(state));
}

/** Clears the decision so the banner is asked again. Used by the footer link. */
export function resetConsent(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* See readConsent. */
  }
  listeners.forEach((fn) => fn('unset'));
}

/**
 * Subscribe to changes. Returns an unsubscribe function.
 *
 * Note there is no "revoke" path that unloads the pixel: once fbevents.js is in
 * the document it cannot be recalled. Withdrawing consent stops all further
 * events and persists the refusal, so the next page view never loads it. That
 * is the honest limit of a client-side gate, and it is why the decision is
 * asked before anything loads rather than after.
 */
export function onConsentChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}
